<svelte:options customElement="dst-at-sidebar" />

<script lang="ts">
	import { baseUrls } from '@/config.ts';
	import { register as registerClosables } from '@/lib/closable.ts';
	import { Logger } from '@/lib/log.ts';
	import { closableInformElement } from '@/stores/header_store.ts';
	import { checkForSp, resurfacePrivacyManager, spStore, stopSpCheck } from '@/stores/sourcePoint_store.ts';
	import { dstHuawei } from '@derstandard/styling-toolkit/src/svg/lib.js';
	import {
		mdiApple,
		mdiApps,
		mdiBellRing,
		mdiChefHat,
		mdiChevronRight,
		mdiClose,
		mdiEmailNewsletter,
		mdiFacebook,
		mdiGoogle,
		mdiGrid,
		mdiHistory,
		mdiInstagram,
		mdiMailbox,
		mdiMessageOutline,
		mdiPodcast,
		mdiRecordCircleOutline,
		mdiRss,
		mdiTwitter,
		mdiYoutube,
	} from '@mdi/js';
	import { afterUpdate, onMount } from 'svelte';
	import '@/global.d.ts';

	const log = Logger('header.DstSidebar');

	let root;
	let showPushSettings = false;
	const baseUrl = baseUrls.editorialweb_at;

	afterUpdate(() => {
		log.debug('Registering push notification closable trigger');
		registerClosables(
			root.querySelector("[data-closable-target='dialog-cleverpush-settings']"),
			$closableInformElement,
		);
	});

	onMount(async () => {
		registerClosables(root, $closableInformElement);

		// Check for SourcePoint script availability
		checkForSp();

		// Stop checking for SourcePoint script
		stopSpCheck($spStore);

		/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
		window.cleverPushInitCallback = (err: any) => {
			if (!err) {
				showPushSettings = true;
			} else if (err.reason === 'unsupported-browser') {
				showPushSettings = false;
				log.error(`CleverPush initialization error - ${err.reason}`);
			}
		};
	});
</script>

<div class="app--header--sidebar" id="app-sidebar" bind:this={root}>
	<div class="app--header--sidebar--content u-scrollbar">
		<div class="app--header--sidebar--header">
			<div class="app-header--sidebar--heading">Nachrichten</div>
			<button type="button" title="Menü ausblenden" data-closable-target="app-sidebar">
				<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
					<path fill="currentColor" d={mdiClose} />
				</svg>
			</button>
		</div>
		<nav aria-label="Haupt-Navigation">
			<ul>
				<li><slot name="search" /></li>
				<li>
					<a href={baseUrl}>
						<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
							<path fill="currentColor" d={mdiApps} />
						</svg>
						Startseite
					</a>
				</li>
				<li>
					<!--
						The community feed only exists in the Austrian edition. While it's hidden from the sidebar in the German edition
						on the web, the link is visible with an app cookie set. To avoid confusion among developers for this artificial situation,
						we point the German edition to the Austrian base URL as well.
					-->
					<a href={new URL('community-feed', import.meta.env.VITE_BASE_URL_EDITORIALWEB).href}>
						<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
							<path fill="currentColor" d={mdiMessageOutline} />
						</svg>
						Community-Feed
					</a>
				</li>
				<li>
					<a href="{baseUrl}/frontpage/latest">
						<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
							<path fill="currentColor" d={mdiHistory} />
						</svg>
						Chronologie
					</a>
				</li>
				<li>
					<details>
						<summary>
							<div>
								<span>Nachrichten &amp; Ressorts</span>
								<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
									<path fill="currentColor" d={mdiChevronRight} />
								</svg>
							</div>
						</summary>
						<ul class="compact">
							<li
								style="--decor: var(--theme--editorial-international--secondary, var(--theme--international--secondary))"
								class="decor"
							>
								<details>
									<summary>
										<div>
											<a href="{baseUrl}/international">International</a>
											<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
												<path fill="currentColor" d={mdiChevronRight} />
											</svg>
										</div>
									</summary>
									<ul>
										<li><a href="{baseUrl}/international/eu">EU</a></li>
										<li><a href="{baseUrl}/international/europa">Europa</a></li>
										<li><a href="{baseUrl}/international/amerika">Amerika</a></li>
										<li><a href="{baseUrl}/international/asien">Asien &amp; Pazifik</a></li>
										<li><a href="{baseUrl}/international/afrika">Afrika</a></li>
										<li><a href="{baseUrl}/international/europa/deutschland">Deutschland</a></li>
										<li><a href="{baseUrl}/international/nahost">Nahost</a></li>
										<li><a href="{baseUrl}/international/amerika/usa">USA</a></li>
									</ul>
								</details>
							</li>
							<li
								style="--decor: var(--theme--editorial-inland--secondary, var(--theme--inland--secondary))"
								class="decor"
							>
								<details>
									<summary>
										<div>
											<a href="{baseUrl}/inland"> Inland</a>
											<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
												<path fill="currentColor" d={mdiChevronRight} />
											</svg>
										</div>
									</summary>
									<ul>
										<li><a href="{baseUrl}/inland/parteien">Parteien</a></li>
										<li><a href="{baseUrl}/inland/parteien/koalition">Koalition</a></li>
										<li><a href="{baseUrl}/inland/sachpolitik">Sachpolitik</a></li>
										<li><a href="{baseUrl}/inland/bundeslaender">Bundesländer</a></li>
										<li><a href="{baseUrl}/inland/coronapolitik">Corona &amp; Politik</a></li>
									</ul>
								</details>
							</li>
							<li
								style="--decor: var(--theme--editorial-wirtschaft--secondary, var(--theme--wirtschaft--secondary))"
								class="decor"
							>
								<details>
									<summary>
										<div>
											<a href="{baseUrl}/wirtschaft">Wirtschaft</a>
											<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
												<path fill="currentColor" d={mdiChevronRight} />
											</svg>
										</div>
									</summary>
									<ul>
										<li><a href="{baseUrl}/wirtschaft/wirtschaftspolitik">Wirtschaftspolitik</a></li>
										<li><a href="{baseUrl}/wirtschaft/unternehmen">Unternehmen</a></li>
										<li><a href="{baseUrl}/wirtschaft/finanzen">Finanzen &amp; Börse</a></li>
									</ul>
								</details>
							</li>
							<li style="--decor: var(--theme--editorial-web--secondary, var(--theme--web--secondary))" class="decor">
								<details>
									<summary>
										<div>
											<a href="{baseUrl}/web">Web</a>
											<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
												<path fill="currentColor" d={mdiChevronRight} />
											</svg>
										</div>
									</summary>
									<ul>
										<li><a href="{baseUrl}/web/games">Games</a></li>
										<li><a href="{baseUrl}/web/innovationen">Innovationen</a></li>
										<li><a href="{baseUrl}/web/itbusiness">IT-Business</a></li>
										<li><a href="{baseUrl}/web/telekom">Telekom</a></li>
										<li><a href="{baseUrl}/web/netzpolitik">Netzpolitik</a></li>
										<li><a href="{baseUrl}/web/webmix">Webmix</a></li>
									</ul>
								</details>
							</li>
							<li
								style="--decor: var(--theme--editorial-sport--secondary, var(--theme--sport--secondary))"
								class="decor"
							>
								<details>
									<summary>
										<div>
											<a href="{baseUrl}/sport">Sport</a>
											<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
												<path fill="currentColor" d={mdiChevronRight} />
											</svg>
										</div>
									</summary>
									<ul>
										<li><a href="{baseUrl}/sport/fussball">Fußball</a></li>
										<li><a href="{baseUrl}/sport/tennis">Tennis</a></li>
										<li><a href="{baseUrl}/sport/motorsport">Motorsport</a></li>
										<li><a href="{baseUrl}/sport/wintersport">Wintersport</a></li>
									</ul>
								</details>
							</li>
							<li
								style="--decor: var(--theme--editorial-panorama--secondary, var(--theme--panorama--secondary))"
								class="decor"
							>
								<details>
									<summary>
										<div>
											<a href="{baseUrl}/panorama">Panorama</a>
											<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
												<path fill="currentColor" d={mdiChevronRight} />
											</svg>
										</div>
									</summary>
									<ul>
										<li><a href="{baseUrl}/panorama/coronavirus">Coronavirus</a></li>
										<li><a href="{baseUrl}/panorama/oesterreich">Österreich</a></li>
										<li><a href="{baseUrl}/panorama/wien">Wien</a></li>
										<li><a href="{baseUrl}/panorama/weltchronik">Weltchronik</a></li>
										<li><a href="{baseUrl}/panorama/gesellschaft">Gesellschaft</a></li>
										<li><a href="{baseUrl}/panorama/oesterreich/gerichtsreportagen">Gerichtsreportagen</a></li>
									</ul>
								</details>
							</li>
							<li
								style="--decor: var(--theme--editorial-kultur--secondary, var(--theme--kultur--secondary))"
								class="decor"
							>
								<details>
									<summary>
										<div>
											<a href="{baseUrl}/kultur">Kultur</a>
											<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
												<path fill="currentColor" d={mdiChevronRight} />
											</svg>
										</div>
									</summary>
									<ul>
										<li><a href="{baseUrl}/kultur/film">Film</a></li>
										<li><a href="{baseUrl}/kultur/musik">Musik</a></li>
										<li><a href="{baseUrl}/kultur/buehne">Bühne</a></li>
										<li><a href="{baseUrl}/kultur/kunst">Kunst</a></li>
										<li><a href="{baseUrl}/kultur/literatur">Literatur</a></li>
										<li><a href="{baseUrl}/kultur/kulturpolitik">Kulturpolitik</a></li>
									</ul>
								</details>
							</li>
							<li style="--decor: var(--theme--editorial-etat--secondary, var(--theme--etat--secondary))" class="decor">
								<details>
									<summary>
										<div>
											<a href="{baseUrl}/etat">Etat</a>
											<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
												<path fill="currentColor" d={mdiChevronRight} />
											</svg>
										</div>
									</summary>
									<ul>
										<li><a href="{baseUrl}/etat/medien">Medien</a></li>
										<li><a href="{baseUrl}/etat/orf">ORF</a></li>
										<li><a href="{baseUrl}/etat/tv">TV</a></li>
										<li><a href="{baseUrl}/etat/werbung">Werbung</a></li>
										<li><a href="{baseUrl}/etat/medien/medien-blogs">Medien-Blogs</a></li>
									</ul>
								</details>
							</li>
							<li
								style="--decor: var(--theme--editorial-wissenschaft--secondary, var(--theme--wissenschaft--secondary))"
								class="decor"
							>
								<details>
									<summary>
										<div>
											<a href="{baseUrl}/wissenschaft">Wissenschaft</a>
											<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
												<path fill="currentColor" d={mdiChevronRight} />
											</svg>
										</div>
									</summary>
									<ul>
										<li><a href="{baseUrl}/wissenschaft/natur">Natur</a></li>
										<li><a href="{baseUrl}/wissenschaft/klima">Klima</a></li>
										<li><a href="{baseUrl}/wissenschaft/weltraum">Weltraum</a></li>
										<li><a href="{baseUrl}/wissenschaft/materie">Materie</a></li>
										<li><a href="{baseUrl}/wissenschaft/zeit">Zeit</a></li>
										<li><a href="{baseUrl}/wissenschaft/mensch">Mensch</a></li>
										<li><a href="{baseUrl}/wissenschaft/forschungspezial">Forschung Spezial</a></li>
									</ul>
								</details>
							</li>
							<li
								style="--decor: var(--theme--editorial-lifestyle--secondary, var(--theme--lifestyle--secondary))"
								class="decor"
							>
								<details>
									<summary>
										<div>
											<a href="{baseUrl}/lifestyle">Lifestyle</a>
											<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
												<path fill="currentColor" d={mdiChevronRight} />
											</svg>
										</div>
									</summary>
									<ul>
										<li><a href="{baseUrl}/lifestyle/essentrinken">Essen &amp; Trinken</a></li>
										<li><a href="{baseUrl}/lifestyle/essbar-rezepte">EssBar Rezepte</a></li>
										<li><a href="{baseUrl}/lifestyle/modebeauty">Mode &amp; Kosmetik</a></li>
										<li><a href="{baseUrl}/lifestyle/uhren">Uhren &amp; Schmuck</a></li>
										<li><a href="{baseUrl}/lifestyle/designinterieur">Design &amp; Interieur</a></li>
										<li><a href="{baseUrl}/lifestyle/raetsel-sudoku">Rätsel &amp; Sudoku</a></li>
									</ul>
								</details>
							</li>
							<li class="decor">
								<a href="{baseUrl}/zukunft">Zukunft</a>
							</li>
							<li
								style="--decor: var(--theme--editorial-gesundheit--secondary, var(--theme--gesundheit--secondary))"
								class="decor"
							>
								<details>
									<summary>
										<div>
											<a href="{baseUrl}/gesundheit">Gesundheit</a>
											<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
												<path fill="currentColor" d={mdiChevronRight} />
											</svg>
										</div>
									</summary>
									<ul>
										<li><a href="{baseUrl}/gesundheit/ernaehrung">Ernährung</a></li>
										<li><a href="{baseUrl}/gesundheit/lebensstil">Lebensstil</a></li>
										<li><a href="{baseUrl}/gesundheit/erkrankungentherapie">Erkrankungen & Therapie</a></li>
										<li><a href="{baseUrl}/gesundheit/psyche">Psyche</a></li>
										<li><a href="{baseUrl}/gesundheit/fragendergesundheit">Fragen der Gesundheit</a></li>
									</ul>
								</details>
							</li>
							<li class="decor">
								<a href="{baseUrl}/recht">Recht</a>
							</li>
							<li class="decor">
								<a href="{baseUrl}/lifestyle/familie">Familie</a>
							</li>
							<li class="decor">
								<a href="{baseUrl}/inland/bildung">Bildung</a>
							</li>
							<li class="decor">
								<a href="{baseUrl}/lifestyle/reisen">Reisen</a>
							</li>
							<li
								style="--decor: var(--theme--editorial-diestandard--secondary, var(--theme--diestandard--secondary))"
								class="decor"
							>
								<a href="{baseUrl}/diestandard">dieStandard</a>
							</li>
							<li
								style="--decor: var(--theme--editorial-karriere--secondary, var(--theme--karriere--secondary))"
								class="decor"
							>
								<details>
									<summary>
										<div>
											<a href="{baseUrl}/karriere">Karriere</a>
											<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
												<path fill="currentColor" d={mdiChevronRight} />
											</svg>
										</div>
									</summary>
									<ul>
										<li><a href="{baseUrl}/karriere/job">Job &amp; Karriere</a></li>
										<li><a href="{baseUrl}/karriere/management">Management</a></li>
										<li><a href="{baseUrl}/karriere/service">Service</a></li>
									</ul>
								</details>
							</li>
							<li
								style="--decor: var(--theme--editorial-immobilien--secondary, var(--theme--immobilien--secondary))"
								class="decor"
							>
								<details>
									<summary>
										<div>
											<a href="{baseUrl}/immobilien">Immobilien</a>
											<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
												<path fill="currentColor" d={mdiChevronRight} />
											</svg>
										</div>
									</summary>
									<ul>
										<li><a href="{baseUrl}/immobilien/bauenwohnen/wohngespraech">Wohngespräche</a></li>
										<li><a href="{baseUrl}/immobilien/bauenwohnen">Bauen &amp; Wohnen</a></li>
										<li><a href="{baseUrl}/immobilien/immogewerbe">Gewerbeimmobilien</a></li>
										<li><a href="{baseUrl}/immobilien/architekturundstadt">Architektur &amp; Stadt</a></li>
										<li><a href="{baseUrl}/immobilien/immobilienservice">Service</a></li>
									</ul>
								</details>
							</li>
							<li class="decor">
								<a href="{baseUrl}/lifestyle/mobilitaet">Mobilität</a>
							</li>
							<li class="decor">
								<a href="{baseUrl}/meinwissensvorsprung">Mein Wissensvorsprung</a>
							</li>
						</ul>
					</details>
				</li>
				<li>
					<details>
						<summary>
							<div>
								<span>Diskurs &amp; Community</span>
								<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
									<path fill="currentColor" d={mdiChevronRight} />
								</svg>
							</div>
						</summary>
						<ul class="compact">
							<li><a href="{baseUrl}/diskurs">Diskurs</a></li>
							<li><a href="{baseUrl}/diskurs/kommentare">Kommentare</a></li>
							<li><a href="{baseUrl}/diskurs/kolumnen">Kolumnen</a></li>
							<li><a href="{baseUrl}/diskurs/debatten">Debatten</a></li>
							<li><a href="{baseUrl}/diskurs/blogs">Blogs</a></li>
							<li><a href="{baseUrl}/diskurs/community">Community</a></li>
							<li><a href="{baseUrl}/diskurs/cartoons">Cartoons</a></li>
							<li><a href="{baseUrl}/diskurs/community/meinforum">meinForum</a></li>
							<li>
								<a href="{baseUrls.editorialweb_at}/story/2000115888550/frequently-asked-questions-zur-community"
									>Community FAQ</a
								>
							</li>
						</ul>
					</details>
				</li>
				<li>
					<ul>
						<li>
							<a href="{baseUrl}/live">
								<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
									<path fill="currentColor" d={mdiRecordCircleOutline} />
								</svg>
								Liveformate
							</a>
						</li>
						<li>
							<a href="{baseUrl}/video">
								<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
									<path fill="currentColor" d={mdiYoutube} />
								</svg>
								Video
							</a>
						</li>
						<li>
							<a href="{baseUrl}/podcast">
								<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
									<path fill="currentColor" d={mdiPodcast} />
								</svg>
								Podcast
							</a>
						</li>
					</ul>
				</li>
				<li>
					<ul>
						<li>
							<a href="{baseUrl}/lifestyle/raetsel-sudoku">
								<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
									<path fill="currentColor" d={mdiGrid} />
								</svg>
								Rätsel & Sudoku
							</a>
						</li>
						<li>
							<a href="{baseUrl}/lifestyle/essbar-rezepte">
								<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
									<path fill="currentColor" d={mdiChefHat} />
								</svg>
								Rezepte
							</a>
						</li>
					</ul>
				</li>
				<li>
					<ul>
						<li>
							<a href="https://about.derstandard.at/newsletter/der-standard-newsletter-anmeldung/" target="_blank">
								<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
									<path fill="currentColor" d={mdiEmailNewsletter} />
								</svg>
								Newsletter
							</a>
						</li>
					</ul>
				</li>
				<li>
					<ul>
						<li>
							<a href="https://derstandard.at/briefkasten/" target="_blank">
								<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
									<path fill="currentColor" d={mdiMailbox} />
								</svg>
								Anonymer Briefkasten
							</a>
						</li>
					</ul>
				</li>
				<li>
					<ul>
						<li>
							<div class="app-header--sidebar--subheading">Social & Feeds</div>
							<ul>
								<li>
									<a href="https://www.instagram.com/derstandardat" target="_blank">
										<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
											<path fill="currentColor" d={mdiInstagram} />
										</svg>
										Instagram
									</a>
								</li>
								<li>
									<a href="https://www.twitter.com/derStandardat" target="_blank">
										<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
											<path fill="currentColor" d={mdiTwitter} />
										</svg>
										Twitter
									</a>
								</li>
								<li>
									<a href="https://www.facebook.com/derStandardat" target="_blank">
										<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
											<path fill="currentColor" d={mdiFacebook} />
										</svg>
										Facebook
									</a>
								</li>
								<li>
									<a href="https://about.derstandard.at/newsletter/rss-feeds/">
										<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
											<path fill="currentColor" d={mdiRss} />
										</svg>
										RSS-Feeds
									</a>
								</li>
								{#if showPushSettings}
									<li>
										<button type="button" data-closable-target="dialog-cleverpush-settings">
											<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
												<path fill="currentColor" d={mdiBellRing} />
											</svg>
											Push-Einstellungen
										</button>
									</li>
								{/if}
							</ul>
						</li>
					</ul>
				</li>
				<li>
					<ul>
						<li>
							<div class="app-header--sidebar--subheading">Apps installieren</div>
							<ul>
								<li>
									<a
										href="https://play.google.com/store/apps/details?id=derstandard.at.istandardx&hl=de"
										target="_blank"
									>
										<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
											<path fill="currentColor" d={mdiGoogle} />
										</svg>
										Nachrichten, Google PlayStore
									</a>
									<a href="https://apps.apple.com/at/app/istandard/id376832518" target="_blank">
										<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
											<path fill="currentColor" d={mdiApple} />
										</svg>
										Nachrichten, Apple AppStore
									</a>
									<a href="https://appgallery.huawei.com/app/C101233753" target="_blank">
										<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
											<path fill="currentColor" d={dstHuawei} />
										</svg>
										Nachrichten, Huawei AppGallery
									</a>
									<a
										href="https://play.google.com/store/apps/details?id=at.apa.pdfwlclient.derstandard&hl=de"
										target="_blank"
									>
										<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
											<path fill="currentColor" d={mdiGoogle} />
										</svg>
										ePaper, Google PlayStore
									</a>
									<a href="https://apps.apple.com/at/app/e-paper-der-standard/id561387166" target="_blank">
										<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
											<path fill="currentColor" d={mdiApple} />
										</svg>
										ePaper, Apple AppStore
									</a>
								</li>
							</ul>
						</li>
					</ul>
				</li>
				{#if $spStore}
					<li>
						<ul>
							<li>
								<button
									type="button"
									on:click={() => resurfacePrivacyManager(import.meta.env.VITE_SOURCEPOINT_PRIVACY_MANAGER_ID_AT)}
									>Privacy Manager</button
								>
							</li>
						</ul>
					</li>
				{/if}
			</ul>
		</nav>
	</div>
	<div class="app--header--sidebar--backdrop" data-closable-target="app-sidebar" />
</div>

<style lang="scss" global>
	@use '../../scss/DstSidebar.scss';
</style>
